import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Plus,
  Star,
  Edit3,
  Trash2,
  ArrowRight,
  Sparkles,
  User,
  Lightbulb,
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { supabase } from '../../../lib/supabase';
import { toast } from 'react-hot-toast';

/**
 * Outline Library - Central hub for all essay outlines
 *
 * Shows outlines from:
 * - Topic Development (AI-generated)
 * - Manual creation
 * - Templates
 *
 * Actions:
 * - Create new outline
 * - Edit existing outline
 * - Copy to draft (quick start)
 * - Develop draft (AI-guided)
 * - Delete outline
 */
export default function OutlineLibrary({ onCreateNew, onEditOutline, onCopyToDraft, onDevelopDraft }) {
  const { currentUser } = useFamilyContext();
  const [outlines, setOutlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'guided' | 'independent' | 'ready'

  // Load outlines
  const loadOutlines = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('college_journey_essay_outlines')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      console.log(`✅ Loaded ${data?.length || 0} outlines`);
      setOutlines(data || []);
    } catch (err) {
      console.error('Error loading outlines:', err);
      toast.error('Failed to load outlines');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    loadOutlines();
  }, [loadOutlines]);

  // Toggle favorite
  const toggleFavorite = async (outlineId, currentlyFavorited) => {
    try {
      const { error } = await supabase
        .from('college_journey_essay_outlines')
        .update({ is_favorite: !currentlyFavorited })
        .eq('id', outlineId);

      if (error) throw error;

      setOutlines(outlines.map(outline =>
        outline.id === outlineId ? { ...outline, is_favorite: !currentlyFavorited } : outline
      ));
    } catch (err) {
      console.error('Error toggling favorite:', err);
      toast.error('Failed to update favorite');
    }
  };

  // Delete outline
  const deleteOutline = async (outlineId) => {
    if (!confirm('Delete this outline? This cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('college_journey_essay_outlines')
        .delete()
        .eq('id', outlineId);

      if (error) throw error;

      setOutlines(outlines.filter(o => o.id !== outlineId));
      toast.success('Outline deleted');
    } catch (err) {
      console.error('Error deleting outline:', err);
      toast.error('Failed to delete outline');
    }
  };

  // Filter outlines
  const filteredOutlines = outlines.filter(outline => {
    if (filter === 'all') return true;
    if (filter === 'guided') return outline.outline_type === 'guided';
    if (filter === 'independent') return outline.outline_type === 'independent';
    if (filter === 'ready') return outline.status === 'ready';
    return true;
  });

  // Group by status/type
  const groupedOutlines = {
    ready: filteredOutlines.filter(o => o.status === 'ready'),
    draft: filteredOutlines.filter(o => o.status === 'draft'),
    completed: filteredOutlines.filter(o => o.status === 'completed')
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading outlines...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 bg-slate-900 rounded-lg p-6">
        <div>
          <h2 className="text-2xl font-bold text-stone-50">Essay Outlines</h2>
          <p className="text-stone-50 mt-1">
            Your outline library - bridge between ideas and drafts
          </p>
        </div>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-teal-500 border border-teal-50 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
        >
          <Plus className="w-5 h-5" />
          New Outline
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-slate-900 text-white'
              : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
          }`}
        >
          All ({outlines.length})
        </button>
        <button
          onClick={() => setFilter('guided')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'guided'
              ? 'bg-slate-900 text-white'
              : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
          }`}
        >
          <Sparkles className="w-4 h-4 inline mr-1" />
          AI-Generated
        </button>
        <button
          onClick={() => setFilter('independent')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'independent'
              ? 'bg-slate-900 text-white'
              : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
          }`}
        >
          <User className="w-4 h-4 inline mr-1" />
          Student-Created
        </button>
        <button
          onClick={() => setFilter('ready')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'ready'
              ? 'bg-slate-900 text-white'
              : 'bg-stone-100 text-slate-700 hover:bg-stone-200'
          }`}
        >
          Ready to Draft
        </button>
      </div>

      {/* Empty State */}
      {filteredOutlines.length === 0 && (
        <div className="text-center py-12 bg-stone-50 rounded-xl border-2 border-dashed border-stone-300">
          <FileText className="w-16 h-16 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-900 mb-2">No outlines yet</h3>
          <p className="text-slate-600 mb-4">
            Create your first outline or develop an essay topic to generate one
          </p>
          <button
            onClick={onCreateNew}
            className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
          >
            Create Outline
          </button>
        </div>
      )}

      {/* Outlines by Status */}
      {groupedOutlines.ready.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-emerald-600 uppercase tracking-wide mb-3">
            Ready to Draft
          </h3>
          <div className="flex flex-col gap-4">
            {groupedOutlines.ready.map(outline => (
              <OutlineCard
                key={outline.id}
                outline={outline}
                onEdit={() => onEditOutline(outline)}
                onCopyToDraft={() => onCopyToDraft(outline)}
                onDevelopDraft={() => onDevelopDraft(outline)}
                onToggleFavorite={toggleFavorite}
                onDelete={deleteOutline}
              />
            ))}
          </div>
        </div>
      )}

      {groupedOutlines.draft.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-3">
            In Progress
          </h3>
          <div className="flex flex-col gap-4">
            {groupedOutlines.draft.map(outline => (
              <OutlineCard
                key={outline.id}
                outline={outline}
                onEdit={() => onEditOutline(outline)}
                onCopyToDraft={() => onCopyToDraft(outline)}
                onDevelopDraft={() => onDevelopDraft(outline)}
                onToggleFavorite={toggleFavorite}
                onDelete={deleteOutline}
              />
            ))}
          </div>
        </div>
      )}

      {groupedOutlines.completed.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wide mb-3">
            Completed
          </h3>
          <div className="flex flex-col gap-4">
            {groupedOutlines.completed.map(outline => (
              <OutlineCard
                key={outline.id}
                outline={outline}
                onEdit={() => onEditOutline(outline)}
                onCopyToDraft={() => onCopyToDraft(outline)}
                onDevelopDraft={() => onDevelopDraft(outline)}
                onToggleFavorite={toggleFavorite}
                onDelete={deleteOutline}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Individual Outline Card
 */
function OutlineCard({ outline, onEdit, onCopyToDraft, onDevelopDraft, onToggleFavorite, onDelete }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getTypeIcon = () => {
    if (outline.outline_type === 'guided') return <Sparkles className="w-4 h-4 text-teal-600" />;
    if (outline.outline_type === 'independent') return <User className="w-4 h-4 text-azure-600" />;
    return <FileText className="w-4 h-4 text-slate-600" />;
  };

  const getTypeLabel = () => {
    if (outline.outline_type === 'guided') return 'AI-Generated';
    if (outline.outline_type === 'independent') return 'Student-Created';
    return 'Template';
  };

  const getStatusColor = () => {
    if (outline.status === 'ready') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (outline.status === 'completed') return 'bg-slate-100 text-slate-600 border-slate-200';
    return 'bg-amber-100 text-amber-700 border-amber-200';
  };

  // Extract outline sections from outline_data
  const outlineData = outline.outline_data || {};
  const outlineSections = [
    { key: 'hook', label: 'Hook' },
    { key: 'context', label: 'Context' },
    { key: 'rising_action', label: 'Rising Action' },
    { key: 'turning_point', label: 'Turning Point' },
    { key: 'resolution', label: 'Resolution' },
    { key: 'closing', label: 'Closing' }
  ].filter(section => outlineData[section.key]);

  return (
    <div className="bg-white rounded-xl border-2 border-slate-900 hover:border-slate-700 transition-all p-5 space-y-4 ">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0 ">
          <div className="flex-shrink-0 mt-0.5">{getTypeIcon()}</div>
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-slate-900 leading-tight mb-1 break-words">
              {outline.title}
            </h4>
            <p className="text-xs text-stone-500">{getTypeLabel()}</p>
          </div>
        </div>
        <button
          onClick={() => onToggleFavorite(outline.id, outline.is_favorite)}
          className="flex-shrink-0 mt-0.5"
        >
          <Star
            className={`w-5 h-5 ${
              outline.is_favorite
                ? 'fill-gold-400 text-gold-400'
                : 'text-stone-300 hover:text-gold-400'
            } transition-colors`}
          />
        </button>
      </div>

      {/* Outline Sections */}
      {outlineSections.length > 0 && (
        <div className="space-y-2">
          {(isExpanded ? outlineSections : outlineSections.slice(0, 3)).map((section) => (
            <div key={section.key} className="border-l-2 border-teal-500 pl-3 py-1">
              <div className="flex items-center gap-2 mb-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                <span className="text-xs font-semibold text-slate-900 uppercase tracking-wide">
                  {section.label}
                </span>
              </div>
              <p className={`text-sm text-slate-700 leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                {outlineData[section.key]}
              </p>
            </div>
          ))}
          {outlineSections.length > 3 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-700 font-medium pl-3 py-1 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" />
                  Show all {outlineSections.length} sections
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Themes - minimal color */}
      {outline.themes && outline.themes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {outline.themes.slice(0, 3).map((theme, i) => (
            <span key={i} className="px-2 py-1 bg-stone-100 border border-stone-300 text-slate-700 rounded text-xs font-medium">
              {theme}
            </span>
          ))}
          {outline.themes.length > 3 && (
            <span className="px-2 py-1 bg-stone-100 text-stone-600 rounded text-xs">
              +{outline.themes.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Status & Date */}
      <div className="flex items-center gap-2 text-xs">
        <span className={`px-2 py-1 rounded border ${getStatusColor()}`}>
          {outline.status}
        </span>
        <span className="text-slate-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(outline.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      </div>

      {/* Actions - Three paths forward */}
      <div className="space-y-2 pt-2 border-t border-stone-200">
        {/* Primary actions row */}
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="flex-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-stone-50 hover:border-slate-400 transition-colors text-xs font-medium flex items-center justify-center gap-1.5"
          >
            <Edit3 className="w-3.5 h-3.5" />
            Edit Outline
          </button>
          <button
            onClick={onCopyToDraft}
            className="flex-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-stone-50 hover:border-slate-400 transition-colors text-xs font-medium flex items-center justify-center gap-1.5"
          >
            <ArrowRight className="w-3.5 h-3.5" />
            Copy to Draft
          </button>
        </div>
        {/* Secondary action row */}
        <div className="flex items-center gap-2">
          <button
            onClick={onDevelopDraft}
            className="flex-1 px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-xs font-medium flex items-center justify-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Develop Draft
          </button>
          <button
            onClick={() => onDelete(outline.id)}
            className="px-3 py-2 text-stone-400 hover:text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
